import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ManualExamMissedAction from './ManualExamMissedAction';

const MAX_FILES = 10;
const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']);

function apiRequest(path, options = {}) {
  const token = localStorage.getItem('school-token') || '';
  return fetch(path, {
    ...options,
    headers: {
      'x-access-token': token,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  }).then(async response => ({
    ok: response.ok,
    status: response.status,
    data: await response.json().catch(() => ({})),
  }));
}

function formatSize(bytes) {
  if (!bytes) return '';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ManualScriptUploadDrawer({ css, uploadCss = css, actionCss = css, actor, examId, examTitle, membershipId, student, onClose, onConfirmed, onMissed }) {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [feeBlocked, setFeeBlocked] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileInputRef = useRef(null);
  const basePath = `/${actor === 'teacher' ? 'teacher' : 'school'}/exams/${examId}/manual/${membershipId}`;
  const progressPath = `/${actor === 'teacher' ? 'teacher' : 'school'}/manual-grading-progress`;

  const loadSubmission = useCallback(async () => {
    setLoading(true);
    const result = await apiRequest(basePath);
    if (result.ok) setSubmission(result.data.submission || null);
    else setError(result.data.error || 'Could not load this manual exam submission.');
    setLoading(false);
  }, [basePath]);

  useEffect(() => { loadSubmission(); }, [loadSubmission]);

  const uploadFiles = async selectedFiles => {
    const files = Array.from(selectedFiles || []);
    if (!files.length) return;
    const currentCount = submission?.files?.length || 0;
    if (currentCount + files.length > MAX_FILES) {
      setError(`You can upload up to ${MAX_FILES} files for this script.`);
      return;
    }
    const invalid = files.find(file => !ACCEPTED_TYPES.has(file.type) || file.size > MAX_FILE_SIZE);
    if (invalid) {
      setError(`${invalid.name} is unsupported or larger than 15 MB. Use JPG, PNG, WEBP, or PDF files.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setProgress({ status: `Uploading ${index + 1} of ${files.length}...`, percent: Math.round((index / files.length) * 100) });
        const urlResult = await apiRequest(`${basePath}/upload-url`, {
          method: 'POST',
          body: JSON.stringify({ fileName: file.name, contentType: file.type, fileSize: file.size }),
        });
        if (!urlResult.ok) throw new Error(urlResult.data.error || 'Could not prepare the upload.');
        const uploadResponse = await fetch(urlResult.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        if (!uploadResponse.ok) throw new Error(`Could not upload ${file.name}.`);
        const confirmResult = await apiRequest(`${basePath}/confirm-upload`, {
          method: 'POST',
          body: JSON.stringify({ key: urlResult.data.key, originalName: file.name, contentType: file.type, size: file.size }),
        });
        if (!confirmResult.ok) throw new Error(confirmResult.data.error || `Could not confirm ${file.name}.`);
        setSubmission(confirmResult.data.submission);
      }
      setProgress(null);
    } catch (uploadError) {
      setError(uploadError.message || 'Upload failed.');
      setProgress(null);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startGrading = async () => {
    setBusy(true);
    setError('');
    setProgress({ status: 'Starting AI grading...', percent: 1 });
    const result = await apiRequest(`${basePath}/grade`, { method: 'POST', body: JSON.stringify({}) });
    if (!result.ok) {
      setFeeBlocked(result.data.feeBlocked === true);
      setError(result.data.error || 'Could not start grading.');
      setBusy(false);
      setProgress(null);
      return;
    }
    setSubmission(result.data.submission);
    const token = encodeURIComponent(localStorage.getItem('school-token') || '');
    const stream = new EventSource(`${progressPath}/${result.data.jobId}?token=${token}`);
    stream.onmessage = async event => {
      const update = JSON.parse(event.data || '{}');
      setProgress({ status: update.message || update.status || 'Processing...', percent: Number(update.progress || 0) });
      if (update.status === 'done' || update.status === 'error') {
        stream.close();
        await loadSubmission();
        setBusy(false);
        setProgress(null);
        if (update.status === 'error' && update.message) setError(update.message);
      }
    };
    stream.onerror = () => {
      stream.close();
      setBusy(false);
      setProgress(null);
      setError('The progress connection closed. Reopen this drawer to check the result.');
    };
  };

  const resetSubmission = async () => {
    setBusy(true);
    setError('');
    const result = await apiRequest(`${basePath}/reset`, { method: 'POST', body: JSON.stringify({}) });
    if (result.ok) setSubmission(result.data.submission);
    else setError(result.data.error || 'Could not reset the script.');
    setBusy(false);
  };

  const confirmResult = async () => {
    setBusy(true);
    setError('');
    const result = await apiRequest(`${basePath}/confirm`, { method: 'POST', body: JSON.stringify({}) });
    if (result.ok) {
      onConfirmed?.();
      onClose();
    } else {
      setFeeBlocked(result.data.feeBlocked === true);
      setError(result.data.error || 'Could not save this result.');
    }
    setBusy(false);
  };

  const draft = submission?.gradingDraft;
  const reviewed = submission?.status === 'reviewed_pending_confirm';
  const confirmed = submission?.status === 'confirmed';
  const canUpload = !busy && !reviewed && !confirmed && (submission?.files?.length || 0) < MAX_FILES;
  const studentName = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student';

  const placeholder = title => (
    <div className={css.drawerSection}>
      <div className={css.drawerSectionTitle}>{title}</div>
      <p style={{ color: '#90a4ae', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
        Available after script upload and AI grading is complete.
      </p>
    </div>
  );

  return (
    <div className={css.drawerOverlay} onClick={onClose}>
      <div className={css.drawer} onClick={event => event.stopPropagation()}>
        <div className={css.drawerHeader}>
          <div>
            <div className={css.drawerTitleRow}>
              <div className={css.drawerTitle}>{studentName}</div>
              <span className={css.betaTag}>Beta release</span>
            </div>
            <div className={css.drawerSubtitle}>{student?.className || ''}{student?.classArm ? ` · ${student.classArm}` : ''} · {examTitle || 'Manual exam'} · Script upload</div>
          </div>
          <button className={css.closeBtn} type="button" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <div className={css.drawerBody}>
          {loading ? <div className={css.drawerLoading}>Loading manual submission...</div> : (
            <>
              {error && <div className={css.inlineError} role={feeBlocked ? 'alert' : undefined} style={feeBlocked ? { fontWeight: 700, borderWidth: 2 } : undefined}>
                {feeBlocked ? 'Result Compilation Blocked: ' : ''}{error}
              </div>}
              <div className={css.drawerSection}>
                <div className={css.drawerSectionTitle}>Script Upload</div>
                {!reviewed && !confirmed && (
                  <>
                    <button
                      type="button"
                      className={uploadCss.dropZone}
                      onClick={() => canUpload && fileInputRef.current?.click()}
                      onDragOver={event => event.preventDefault()}
                      onDrop={event => { event.preventDefault(); if (canUpload) uploadFiles(event.dataTransfer.files); }}
                      disabled={!canUpload}
                    >
                      <span className={uploadCss.dropZonePlaceholder}><UploadFileIcon /></span>
                      <span className={uploadCss.dropZoneText}>{busy ? 'Processing...' : 'Add script pages'}</span>
                      <span className={uploadCss.dropZoneHint}>JPG, PNG, WEBP, or PDF · up to 10 files · 15 MB each</span>
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment" multiple hidden onChange={event => uploadFiles(event.target.files)} />
                  </>
                )}
                {(submission?.files || []).length > 0 && (
                  <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                    {submission.files.map(file => <div key={`${file.key || file.originalName}-${file.pageOrder}`} className={uploadCss.dropZoneFile}>{file.originalName}<span>{formatSize(file.size)}</span></div>)}
                  </div>
                )}
                {progress && <div style={{ marginTop: 12, color: '#1a6e73', fontSize: '0.82rem' }}>{progress.status} {progress.percent}%</div>}
                {!reviewed && !confirmed && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
                  <button type="button" className={actionCss.btnPrimary} disabled={busy || feeBlocked || !(submission?.files?.length)} onClick={startGrading}><AutorenewIcon fontSize="small" /> Start AI Grading</button>
                  <ManualExamMissedAction
                    css={css}
                    actionCss={actionCss}
                    actor={actor}
                    examMode="manual"
                    examId={examId}
                    examTitle={examTitle}
                    membershipId={membershipId}
                    student={student}
                    disabled={busy}
                    onMarked={() => { onMissed?.(); onClose(); }}
                  />
                </div>}
              </div>

              <div className={css.perfCardRow}>
                <div className={css.perfCard}><div className={css.perfCardLabel}>Score</div><div className={css.perfCardValue}>{draft?.score ?? '—'}</div></div>
                <div className={css.perfCard}><div className={css.perfCardLabel}>Percentage</div><div className={css.perfCardValue}>{draft?.percentage != null ? `${draft.percentage}%` : '—'}</div></div>
                <div className={css.perfCard}><div className={css.perfCardLabel}>Time Taken</div><div className={css.perfCardValue}>—</div></div>
                <div className={css.perfCard}><div className={css.perfCardLabel}>Result</div><div className={css.perfCardValue} style={{ color: draft?.pass ? '#2e7d32' : draft ? '#c62828' : '#90a4ae' }}>{draft ? (draft.pass ? 'Pass' : 'Fail') : '—'}</div></div>
              </div>

              {reviewed ? (
                <>
                  <div className={css.drawerSection}>
                    <div className={css.drawerSectionTitle}>Topic Breakdown</div>
                    <div className={css.topicPillRow}>{(draft.topicBreakdown || []).map(topic => <div key={topic.topic} className={css.topicPill}><span className={css.topicPillName}>{topic.topic}</span><span className={css.topicPillStat}>{topic.correct}/{topic.total}</span></div>)}</div>
                  </div>
                  <div className={css.drawerSection}>
                    <div className={css.drawerSectionTitle}>Question Analysis</div>
                    <div className={css.tableWrap}><table className={css.table}><thead><tr><th>#</th><th>Question</th><th>Answer</th><th>Result</th><th>Topic</th></tr></thead><tbody>{(draft.questionAnalysis || []).map(question => <tr key={question.number}><td>{question.number}</td><td className={css.qText}>{question.question}</td><td>{question.chose || '—'}</td><td>{question.correct ? 'Correct' : 'Review'}</td><td>{question.topic || 'General'}</td></tr>)}</tbody></table></div>
                  </div>
                  <div className={css.drawerSection}>
                    <div className={css.drawerSectionTitle}>Integrity Report</div>
                    <p style={{ color: '#607d8b', fontSize: '0.82rem', margin: 0 }}>Manual exam taken; no integrity report is available.</p>
                  </div>
                  <div className={css.drawerSection}>
                    <div className={css.drawerSectionTitle}>Study Plan</div>
                    <p style={{ color: '#607d8b', fontSize: '0.82rem', margin: 0 }}>The study plan will be available after this result is saved.</p>
                  </div>
                  <div className={css.modalFooter} style={{ position: 'sticky', bottom: 0, background: '#fff', gap: 8, flexWrap: 'wrap' }}>
                    <ManualExamMissedAction
                      css={css}
                      actionCss={actionCss}
                      actor={actor}
                      examMode="manual"
                      examId={examId}
                      examTitle={examTitle}
                      membershipId={membershipId}
                      student={student}
                      disabled={busy}
                      onMarked={() => { onMissed?.(); onClose(); }}
                    />
                    <button type="button" className={actionCss.btnSecondary} disabled={busy} onClick={resetSubmission}>Retry From Start</button>
                    <button type="button" className={actionCss.btnSecondary} disabled={busy || feeBlocked} onClick={startGrading}><AutorenewIcon fontSize="small" /> Retry Grading</button>
                    <button type="button" className={actionCss.btnPrimary} disabled={busy || feeBlocked} onClick={confirmResult}><CheckCircleIcon fontSize="small" /> Confirm &amp; Save Result</button>
                  </div>
                </>
              ) : (
                <>{placeholder('Study Plan')}{placeholder('Topic Breakdown')}{placeholder('Question Analysis')}{placeholder('Integrity Report')}</>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
